import type { GamePhase } from "@dawn/engine";
import { createLeaveGuard, type LeaveGuardHandle } from "./leaveGuard.ts";
import { isResumableWorldPhase } from "./worldSaveStorage.ts";

export interface WorldLeaveHostDeps {
  getPhase: () => GamePhase;
  /** When false, history/unload listeners stay installed but do nothing. Defaults to always active. */
  isActive?: () => boolean;
  persistSave: () => boolean;
  onExit: () => void;
  requestExit: () => void;
}

export interface WorldLeaveHost {
  handle: LeaveGuardHandle;
  persistSessionSave(): boolean;
  shouldConfirmLeave(): boolean;
  onConfirmLeave(): boolean;
}

export function createWorldLeaveHost(deps: WorldLeaveHostDeps): WorldLeaveHost {
  const persistSessionSave = (): boolean => deps.persistSave();
  const isActive = deps.isActive ?? (() => true);

  const handle = createLeaveGuard({
    isActive,
    isResumable: () => isResumableWorldPhase(deps.getPhase()),
    saveGame: () => {
      persistSessionSave();
    },
    requestConfirmedExit: deps.requestExit,
  });

  return {
    handle,
    persistSessionSave,
    shouldConfirmLeave: () => isResumableWorldPhase(deps.getPhase()),
    onConfirmLeave: () => {
      if (!persistSessionSave()) {
        return false;
      }

      // Disarm before reload/assign so the native beforeunload prompt does not appear.
      const pendingNavigation = handle.pendingNavigationUrl;
      handle.allowUnload();
      if (pendingNavigation) {
        handle.completePendingNavigation();
      } else {
        deps.onExit();
      }
      return true;
    },
  };
}
